import {
  COMMON_TIME_SIGNATURES,
  COMMON_KEY_SIGNATURES,
  DEFAULT_TIME_SIGNATURE,
  DEFAULT_KEY_SIGNATURE,
  type LiveComposerEvent,
  type LiveComposerPedalEvent,
} from '../../CreateMusicXml';
import { clonePerformanceTake, isPerformanceTake, type PerformanceTake } from './performance';
export type BuilderMode = 'capture' | 'edit' | 'practice';
export type RecordingHandMode = 'both' | 'right' | 'left';
/** Free time trusts the performance's own pulse; a click anchors capture to the metronome. */
export type CaptureClockMode = 'free' | 'click';
const MIN_PLAYBACK_TEMPO_BPM = 40;
const MAX_PLAYBACK_TEMPO_BPM = 220;
const DEFAULT_PLAYBACK_TEMPO_BPM = 104;
const DEFAULT_CAPTURE_TEMPO_MIN_BPM = 60;
const DEFAULT_CAPTURE_TEMPO_MAX_BPM = 180;
export const DEFAULT_DRAFT_TITLE = 'Untitled';
/** Earlier placeholder titles; a draft still carrying one is treated as untitled. */
const LEGACY_DEFAULT_DRAFT_TITLES = new Set(['Untitled Repertoire Draft', 'Untitled composition']);
const DEFAULT_BUILDER_MODE: BuilderMode = 'edit';
const PRERENDERED_MEASURE_COUNT = 4;
const MAX_DRAFT_MEASURE_COUNT = 96;
const DEFAULT_DRAFT_DURATION_DIVISIONS = 4;
const DRAFT_DURATION_OPTIONS = [0.25, 0.5, 1, 2, 4, 6, 8, 16].map((divisions) => ({ divisions }));
const clampTempoBpm = (
  tempo: number,
  minTempo = MIN_PLAYBACK_TEMPO_BPM,
  maxTempo = MAX_PLAYBACK_TEMPO_BPM,
) => Math.max(minTempo, Math.min(maxTempo, Math.round(tempo)));

export type ComposerDraftSnapshot = {
  performanceTakes?: PerformanceTake[];
  selectedEventIds?: string[];
  selectedNoteTarget?: { eventId: string; midi: number } | null;
  loop?: { startDivisions: number; endDivisions: number } | null;
  selectedPassage?: { startDivisions: number; endDivisions: number } | null;
  capturedEvents: LiveComposerEvent[];
  capturedPedalEvents: LiveComposerPedalEvent[];
  draftTitle: string;
  builderMode: BuilderMode;
  timeSignatureId: string;
  keySignatureId: string;
  draftMeasureCount: number;
  selectedDurationDivisions: number;
  recordingCursorDivisions: number;
  recordingHandMode: RecordingHandMode;
  playbackTempo: number;
  metronomeEnabled: boolean;
  editAuditionEnabled: boolean;
  captureTempoAutoEnabled: boolean;
  captureTempoBpm: number;
  captureTempoMinBpm: number;
  captureTempoMaxBpm: number;
  captureTempoConfidence: number;
  captureClockMode?: CaptureClockMode;
};

export const cloneLiveComposerEvents = (events: LiveComposerEvent[]) =>
  events.map((event) => ({
    ...event,
    midiNotes: [...event.midiNotes],
    accidentalSpellings: event.accidentalSpellings ? { ...event.accidentalSpellings } : undefined,
    velocities: event.velocities ? { ...event.velocities } : undefined,
    performanceNoteIds: event.performanceNoteIds ? [...event.performanceNoteIds] : undefined,
  }));

export const cloneLiveComposerPedalEvents = (events: LiveComposerPedalEvent[]) =>
  events.map((event) => ({ ...event }));

export const cloneDraftSnapshot = (snapshot: ComposerDraftSnapshot): ComposerDraftSnapshot => ({
  ...snapshot,
  selectedEventIds: [...(snapshot.selectedEventIds ?? [])],
  selectedNoteTarget: snapshot.selectedNoteTarget ? { ...snapshot.selectedNoteTarget } : null,
  loop: snapshot.loop ? { ...snapshot.loop } : null,
  selectedPassage: snapshot.selectedPassage ? { ...snapshot.selectedPassage } : null,
  performanceTakes: snapshot.performanceTakes?.map(clonePerformanceTake),
  capturedEvents: cloneLiveComposerEvents(snapshot.capturedEvents),
  capturedPedalEvents: cloneLiveComposerPedalEvents(snapshot.capturedPedalEvents),
});

const isRecordingHandMode = (value: unknown): value is RecordingHandMode =>
  value === 'both' || value === 'right' || value === 'left';

const isBuilderMode = (value: unknown): value is BuilderMode =>
  value === 'capture' || value === 'edit' || value === 'practice';

const clampDraftTempo = (tempo: unknown) =>
  typeof tempo === 'number' && Number.isFinite(tempo)
    ? Math.max(MIN_PLAYBACK_TEMPO_BPM, Math.min(MAX_PLAYBACK_TEMPO_BPM, Math.round(tempo)))
    : DEFAULT_PLAYBACK_TEMPO_BPM;

const clampStoredCaptureTempo = (tempo: unknown) =>
  typeof tempo === 'number' && Number.isFinite(tempo)
    ? clampTempoBpm(tempo, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
    : DEFAULT_PLAYBACK_TEMPO_BPM;

const clampStoredCaptureConfidence = (confidence: unknown) =>
  typeof confidence === 'number' && Number.isFinite(confidence)
    ? Math.max(0, Math.min(1, confidence))
    : 0;

const normalizeDraftMeasureCount = (measureCount: unknown) =>
  typeof measureCount === 'number' && Number.isFinite(measureCount)
    ? Math.max(1, Math.min(MAX_DRAFT_MEASURE_COUNT, Math.round(measureCount)))
    : PRERENDERED_MEASURE_COUNT;

export const normalizeDraftSnapshot = (value: unknown): ComposerDraftSnapshot | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<ComposerDraftSnapshot>;
  if (!Array.isArray(raw.capturedEvents) || !Array.isArray(raw.capturedPedalEvents)) return null;
  if (
    raw.performanceTakes !== undefined &&
    (!Array.isArray(raw.performanceTakes) ||
      !raw.performanceTakes.every(isPerformanceTake) ||
      new Set(raw.performanceTakes.map((take) => take.id)).size !== raw.performanceTakes.length)
  )
    return null;
  const ids = new Set<string>();
  for (const event of raw.capturedEvents) {
    if (
      !event ||
      typeof event.id !== 'string' ||
      ids.has(event.id) ||
      !Array.isArray(event.midiNotes) ||
      !event.midiNotes.every((midi) => Number.isInteger(midi) && midi >= 0 && midi <= 127) ||
      !Number.isFinite(event.durationDivisions) ||
      event.durationDivisions <= 0 ||
      (event.startDivisions !== undefined &&
        (!Number.isFinite(event.startDivisions) || event.startDivisions < 0))
    )
      return null;
    ids.add(event.id);
  }
  for (const event of raw.capturedPedalEvents) {
    if (
      !event ||
      typeof event.id !== 'string' ||
      !Number.isFinite(event.positionDivisions) ||
      event.positionDivisions < 0 ||
      typeof event.pedalDown !== 'boolean'
    )
      return null;
  }
  const selectedDuration = DRAFT_DURATION_OPTIONS.some(
    (option) => option.divisions === raw.selectedDurationDivisions,
  )
    ? Number(raw.selectedDurationDivisions)
    : DEFAULT_DRAFT_DURATION_DIVISIONS;
  const timeSignatureId = COMMON_TIME_SIGNATURES.some(
    (timeSignature) => timeSignature.id === raw.timeSignatureId,
  )
    ? String(raw.timeSignatureId)
    : DEFAULT_TIME_SIGNATURE.id;
  const keySignatureId = COMMON_KEY_SIGNATURES.some(
    (keySignature) => keySignature.id === raw.keySignatureId,
  )
    ? String(raw.keySignatureId)
    : DEFAULT_KEY_SIGNATURE.id;

  return {
    selectedPassage:
      raw.selectedPassage &&
      Number.isFinite(raw.selectedPassage.startDivisions) &&
      Number.isFinite(raw.selectedPassage.endDivisions) &&
      raw.selectedPassage.endDivisions > raw.selectedPassage.startDivisions
        ? { ...raw.selectedPassage }
        : null,
    performanceTakes: raw.performanceTakes?.map(clonePerformanceTake) ?? [],
    selectedEventIds: Array.isArray(raw.selectedEventIds)
      ? raw.selectedEventIds.filter((id) => ids.has(id))
      : [],
    selectedNoteTarget:
      raw.selectedNoteTarget && ids.has(raw.selectedNoteTarget.eventId)
        ? raw.selectedNoteTarget
        : null,
    loop:
      raw.loop &&
      Number.isFinite(raw.loop.startDivisions) &&
      Number.isFinite(raw.loop.endDivisions) &&
      raw.loop.startDivisions >= 0 &&
      raw.loop.endDivisions > raw.loop.startDivisions
        ? { ...raw.loop }
        : null,
    capturedEvents: Array.isArray(raw.capturedEvents)
      ? cloneLiveComposerEvents(raw.capturedEvents)
      : [],
    capturedPedalEvents: Array.isArray(raw.capturedPedalEvents)
      ? cloneLiveComposerPedalEvents(raw.capturedPedalEvents)
      : [],
    draftTitle:
      typeof raw.draftTitle === 'string' &&
      raw.draftTitle.trim() &&
      !LEGACY_DEFAULT_DRAFT_TITLES.has(raw.draftTitle.trim())
        ? raw.draftTitle
        : DEFAULT_DRAFT_TITLE,
    builderMode: isBuilderMode(raw.builderMode) ? raw.builderMode : DEFAULT_BUILDER_MODE,
    timeSignatureId,
    keySignatureId,
    draftMeasureCount: normalizeDraftMeasureCount(raw.draftMeasureCount),
    selectedDurationDivisions: selectedDuration,
    recordingCursorDivisions:
      typeof raw.recordingCursorDivisions === 'number' &&
      Number.isFinite(raw.recordingCursorDivisions)
        ? Math.max(0, raw.recordingCursorDivisions)
        : 0,
    recordingHandMode: isRecordingHandMode(raw.recordingHandMode) ? raw.recordingHandMode : 'both',
    playbackTempo: clampDraftTempo(raw.playbackTempo),
    metronomeEnabled: Boolean(raw.metronomeEnabled),
    editAuditionEnabled:
      typeof raw.editAuditionEnabled === 'boolean' ? raw.editAuditionEnabled : false,
    captureTempoAutoEnabled:
      typeof raw.captureTempoAutoEnabled === 'boolean' ? raw.captureTempoAutoEnabled : true,
    captureTempoBpm: clampStoredCaptureTempo(raw.captureTempoBpm),
    captureTempoMinBpm:
      typeof raw.captureTempoMinBpm === 'number' && Number.isFinite(raw.captureTempoMinBpm)
        ? clampTempoBpm(raw.captureTempoMinBpm, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
        : DEFAULT_CAPTURE_TEMPO_MIN_BPM,
    captureTempoMaxBpm:
      typeof raw.captureTempoMaxBpm === 'number' && Number.isFinite(raw.captureTempoMaxBpm)
        ? clampTempoBpm(raw.captureTempoMaxBpm, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
        : DEFAULT_CAPTURE_TEMPO_MAX_BPM,
    captureTempoConfidence: clampStoredCaptureConfidence(raw.captureTempoConfidence),
    captureClockMode: raw.captureClockMode === 'click' ? 'click' : 'free',
  };
};

export const draftHasMaterial = (snapshot: ComposerDraftSnapshot) =>
  snapshot.capturedEvents.length > 0 ||
  snapshot.capturedPedalEvents.length > 0 ||
  snapshot.draftTitle !== DEFAULT_DRAFT_TITLE ||
  snapshot.builderMode !== DEFAULT_BUILDER_MODE ||
  snapshot.timeSignatureId !== DEFAULT_TIME_SIGNATURE.id ||
  snapshot.keySignatureId !== DEFAULT_KEY_SIGNATURE.id ||
  snapshot.draftMeasureCount !== PRERENDERED_MEASURE_COUNT ||
  snapshot.selectedDurationDivisions !== DEFAULT_DRAFT_DURATION_DIVISIONS ||
  snapshot.recordingCursorDivisions !== 0 ||
  snapshot.recordingHandMode !== 'both' ||
  snapshot.playbackTempo !== DEFAULT_PLAYBACK_TEMPO_BPM ||
  snapshot.metronomeEnabled ||
  snapshot.editAuditionEnabled ||
  !snapshot.captureTempoAutoEnabled ||
  snapshot.captureTempoBpm !== DEFAULT_PLAYBACK_TEMPO_BPM ||
  snapshot.captureTempoMinBpm !== DEFAULT_CAPTURE_TEMPO_MIN_BPM ||
  snapshot.captureTempoMaxBpm !== DEFAULT_CAPTURE_TEMPO_MAX_BPM ||
  snapshot.captureTempoConfidence > 0;

export const getDraftSnapshotSignature = (snapshot: ComposerDraftSnapshot) =>
  JSON.stringify({
    events: snapshot.capturedEvents.map((event) => ({
      id: event.id,
      midiNotes: event.midiNotes,
      accidentalSpellings: event.accidentalSpellings,
      source: event.source,
      velocities: event.velocities,
      performanceNoteIds: event.performanceNoteIds,
      restBeforeDivisions: event.restBeforeDivisions,
      durationDivisions: event.durationDivisions,
      startDivisions: event.startDivisions,
      staff: event.staff,
      endedAtMs: event.endedAtMs,
      adaptiveTiming: event.adaptiveTiming,
      rawStartedAtMs: event.rawStartedAtMs,
      rawEndedAtMs: event.rawEndedAtMs,
      rawCaptureSessionStartedAtMs: event.rawCaptureSessionStartedAtMs,
      rawCaptureSessionStartDivisions: event.rawCaptureSessionStartDivisions,
      slurStart: event.slurStart,
      slurStop: event.slurStop,
      dynamic: event.dynamic,
    })),
    pedalEvents: snapshot.capturedPedalEvents.map((event) => ({
      id: event.id,
      pedalDown: event.pedalDown,
      value: event.value,
      channel: event.channel,
      positionDivisions: event.positionDivisions,
      adaptiveTiming: event.adaptiveTiming,
      rawCaptureSessionStartedAtMs: event.rawCaptureSessionStartedAtMs,
      rawCaptureSessionStartDivisions: event.rawCaptureSessionStartDivisions,
    })),
    draftTitle: snapshot.draftTitle,
    timeSignatureId: snapshot.timeSignatureId,
    keySignatureId: snapshot.keySignatureId,
    draftMeasureCount: snapshot.draftMeasureCount,
    playbackTempo: snapshot.playbackTempo,
    captureTempoAutoEnabled: snapshot.captureTempoAutoEnabled,
    captureTempoBpm: snapshot.captureTempoBpm,
    captureTempoMinBpm: snapshot.captureTempoMinBpm,
    captureTempoMaxBpm: snapshot.captureTempoMaxBpm,
  });

export const createEmptySnapshot = () =>
  normalizeDraftSnapshot({ capturedEvents: [], capturedPedalEvents: [] })!;
